import React, { useRef, useState } from 'react';
import { useController, FieldValues, UseControllerProps } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import composeRefs from '@seznam/compose-react-refs';

import { classNames } from 'primereact/utils';
import { Calendar } from 'primereact/calendar';

import CustomLabel from './Label';
import { Button } from '../Button';

interface Props<T extends FieldValues> extends UseControllerProps<T> {
	placeholder?: string;
	label?: string;
	required?: boolean;
	withHours?: boolean;
	minDate?: Date;
	disabled?: boolean;
	className?: string;
	onManualConfirm?: () => void;
}
const ValidateCalendar = <T extends FieldValues>(props: Props<T>) => {
	const {
		name,
		label,
		placeholder,
		control,
		required = false,
		rules,
		withHours,
		minDate,
		disabled,
		className,
		onManualConfirm,
	} = props;

	const { t } = useTranslation();

	const {
		field,
		fieldState: { error },
	} = useController({
		name,
		control,
		rules: {
			required: required ? `${label ?? placeholder} ${t('misc.forms.isRequired')}` : false,
			...rules,
		},
	});

	const [comingValue, setComingValue] = useState<Date | undefined>(undefined);

	const calendarRef = useRef<Calendar>();
	const hasManualConfirmFromSave = useRef(false);
	const value = comingValue ?? field.value;

	return (
		<div className={classNames('overflow-hidden', className)}>
			{label && <CustomLabel name={name}>{label}</CustomLabel>}

			<Calendar
				minDate={minDate}
				disabled={disabled}
				id={field.name}
				inputClassName="input-base"
				className={classNames({ 'p-invalid': error }, 'w-full p-datepicker-tiny')}
				placeholder={placeholder}
				showTime={withHours}
				onHide={() => {
					if (!hasManualConfirmFromSave.current) {
						onManualConfirm?.();
					}
					hasManualConfirmFromSave.current = false;
					setComingValue(undefined);
				}}
				panelClassName="p-datepicker-tiny"
				footerTemplate={() => (
					<div className="flex flex-row gap-1 justify-content-between p-2">
						<Button
							label={t('actions.cancel')}
							onClick={() => {
								if (calendarRef?.current) {
									calendarRef.current.hide();
								}
							}}
							className="p-button-outlined"
						/>
						<div className="flex flex-row gap-1">
							<Button
								label={t('actions.clear')}
								onClick={() => {
									setComingValue(undefined);
									if (calendarRef?.current) {
										const date = calendarRef.current?.getCurrentDateTime();
										if (date instanceof Date) {
											field.onChange('');
										}
										calendarRef.current.hide();
									}
								}}
								className="p-button-outlined"
							/>
							<Button
								label={t('actions.save')}
								onClick={() => {
									if (comingValue !== undefined) {
										const date = comingValue;
										field.onChange(date);
									}
									hasManualConfirmFromSave.current = true;
									onManualConfirm?.();
									calendarRef?.current?.hide();
								}}
							/>
						</div>
					</div>
				)}
				stepMinute={15}
				{...field}
				onChange={({ value: nextValue }) => {
					if (nextValue instanceof Date) {
						setComingValue(nextValue);
						return;
					}
					setComingValue(undefined);
				}}
				value={value}
				ref={composeRefs(field.ref, calendarRef)}
			/>
			{error && <small className="p-error font-bold ml-3">{error.message}</small>}
		</div>
	);
};

export default ValidateCalendar;
